import React from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FdsDecisionResult } from "@kkp/api-client";
import {
  PageContainer,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Button,
  toast,
} from "@kkp/ui";
import { formatCurrency } from "@kkp/app-kit";
import {
  FdsInactiveBanner,
  Field,
  formatDateTime,
  useCapabilities,
} from "@kkp/features";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import { Field as FormField } from "../../ui/Input";
import { ApiError } from "../../api/client";
import { rerouteFromAnomaly } from "../../api/bulk-queue";
import { decideFdsCase, getFdsCase, type FdsAction } from "../../api/fds";
import {
  ACTION_LABELS,
  ACTION_VARIANT,
  allowedActions,
  needsAuthorityRef,
  deviationLabel,
  reasonLabel,
} from "./reasons";
import { OverdueBadge, ScopePill, StagePill } from "./shared";

const FDS_CASE_KEY = (id: string) => ["fds-case", id];

export function FdsCaseDetail({ caseId }: { caseId: string }) {
  const queryClient = useQueryClient();
  const { data: capabilities } = useCapabilities();
  const fdsEnabled = capabilities?.fds === true;
  const { data, isLoading, isError } = useQuery({
    queryKey: FDS_CASE_KEY(caseId),
    queryFn: () => getFdsCase(caseId),
    enabled: Boolean(caseId),
    refetchOnMount: "always",
  });
  const [pendingAction, setPendingAction] = React.useState<FdsAction | null>(
    null,
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: FDS_CASE_KEY(caseId) });
    void queryClient.invalidateQueries({ queryKey: ["fds-cases"] });
  };

  const acknowledgeMutation = useMutation({
    mutationFn: () => decideFdsCase(caseId, "acknowledge"),
    onSuccess: () => {
      toast.success("Kasus diakui.", { title: "Tersimpan" });
      refresh();
    },
    onError: (err: unknown) => {
      toast.danger(err instanceof ApiError ? err.message : "Gagal menyimpan.", {
        title: "Error",
      });
    },
  });

  const rerouteMutation = useMutation({
    mutationFn: (request: { bulkTxId: string; amountIdr: string }) =>
      rerouteFromAnomaly({ ...request, reference: `reroute:${caseId}` }),
    onSuccess: (outcome) => {
      if (outcome.outcome === "Executed") {
        toast.success("Dana dikembalikan dan instruksi dieksekusi.", {
          title: "Tersimpan",
        });
      } else {
        toast.success(outcome.reason ?? "Dana dikembalikan ke batch.", {
          title: "Tersimpan",
        });
      }
      refresh();
    },
    onError: (err: unknown) => {
      toast.danger(err instanceof ApiError ? err.message : "Gagal menyimpan.", {
        title: "Error",
      });
    },
  });

  if (isError) {
    return (
      <div className="text-sm text-[color:var(--kkp-text-muted)]">
        Gagal memuat kasus.
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="text-sm text-[color:var(--kkp-text-muted)]">Memuat…</div>
    );
  }

  const { case: item, trail } = data;
  const actions = allowedActions(item.stage);

  return (
    <div className="grid gap-4">
      <div className="text-sm text-[color:var(--kkp-text-muted)]">
        <Link to="/fds" className="hover:underline">
          FDS
        </Link>{" "}
        / Antrean / {item.bulkTxId}
      </div>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle as="h2">
              {item.scope === "WholeBatch"
                ? "Peninjauan seluruh batch"
                : "Peninjauan kasus penerima"}
            </CardTitle>
            <ScopePill scope={item.scope} />
            <StagePill stage={item.stage} />
            {item.overdue ? <OverdueBadge /> : null}
          </div>
        </CardHeader>
        <CardBody className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          <Field label="Perusahaan" value={item.companyName} />
          <Field label="Bulk transfer" value={item.bulkTxId} />
          <Field label="Channel" value={data.channel} />
          <Field label="Total penerima" value={String(data.totalRecipients)} />
          {item.scope === "Recipient" ? (
            <>
              <Field label="Penerima" value={item.recipientName ?? "—"} />
              <Field label="Employee ID" value={item.employeeId ?? "—"} />
            </>
          ) : null}
          <Field label="Baseline version" value={item.baselineVersion ?? "—"} />
          {item.blockOrder ? (
            <Field label="Order blokir" value={item.blockOrder} />
          ) : null}
        </CardBody>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle as="h3">Ringkasan dana</CardTitle>
          </CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Pokok tertahan"
              value={formatCurrency(item.heldPrincipalIdr)}
            />
            <Field
              label="Pokok dibandingkan"
              value={
                item.observedPrincipalIdr === null
                  ? "-"
                  : formatCurrency(item.observedPrincipalIdr)
              }
            />
            <Field
              label="Pokok disetujui (baseline)"
              value={
                item.approvedPrincipalIdr === null
                  ? "Tidak ada baseline"
                  : formatCurrency(item.approvedPrincipalIdr)
              }
            />
            <Field
              label="Selisih"
              value={
                deviationLabel(
                  item.observedPrincipalIdr,
                  item.approvedPrincipalIdr,
                ) ?? "-"
              }
            />
            <Field
              label="Biaya tertahan"
              value={formatCurrency(item.heldFeeIdr)}
            />
            <Field
              label="Dana dibutuhkan"
              value={formatCurrency(data.fundingRequiredIdr)}
            />
            <Field
              label="Dana diterima"
              value={formatCurrency(data.fundingReceivedIdr)}
            />
            <Field
              label="Dana tertahan (akun)"
              value={formatCurrency(data.fundingHeldIdr)}
            />
            <Field label="Status batch" value={item.batchStatus} />
            <Field
              label="Di akun Anomaly"
              value={formatCurrency(data.inAnomalyIdr)}
            />
            {item.stage === "Released" &&
            item.batchStatus === "Pending" &&
            Number(data.inAnomalyIdr) > 0 ? (
              <div className="sm:col-span-2">
                <Button
                  variant="primary"
                  disabled={!fdsEnabled || rerouteMutation.isPending}
                  onClick={() =>
                    rerouteMutation.mutate({
                      bulkTxId: item.bulkTxId,
                      amountIdr: data.inAnomalyIdr,
                    })
                  }
                >
                  Kembalikan dana dari Anomaly
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h3">Status operasional</CardTitle>
          </CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-2">
            <Field label="Diajukan" value={formatDateTime(item.raisedAt)} />
            <Field
              label="Tenggat pengakuan"
              value={formatDateTime(item.ackDueAt)}
            />
            <Field
              label="Tenggat keputusan"
              value={formatDateTime(item.decisionDueAt)}
            />
            {item.escalatedAt ? (
              <Field
                label="Dieskalasi"
                value={formatDateTime(item.escalatedAt)}
              />
            ) : null}
            {item.closedAt ? (
              <Field label="Ditutup" value={formatDateTime(item.closedAt)} />
            ) : null}
            <div className="sm:col-span-2">
              <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
                Alasan
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {item.reasons.length === 0 ? (
                  <span className="text-sm text-[color:var(--kkp-text-muted)]">
                    —
                  </span>
                ) : (
                  item.reasons.map((r) => (
                    <span
                      key={r}
                      className="rounded-full border border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.04)] px-2 py-0.5 text-[11px]"
                    >
                      {reasonLabel(r)}
                    </span>
                  ))
                )}
              </div>
              {item.complianceReview ? (
                <div className="mt-2 text-[11px] font-semibold text-amber-200">
                  Tinjauan kepatuhan diperlukan
                </div>
              ) : null}
            </div>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle as="h3">Jejak keputusan</CardTitle>
        </CardHeader>
        <CardBody>
          {trail.length === 0 ? (
            <div className="text-sm text-[color:var(--kkp-text-muted)]">
              Belum ada keputusan tercatat.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-[color:var(--kkp-border)] text-left text-xs text-[color:var(--kkp-text-muted)]">
                    <th className="py-2 pr-3">Aksi</th>
                    <th className="py-2 pr-3">Tahap</th>
                    <th className="py-2 pr-3">Petugas</th>
                    <th className="py-2 pr-3">Alasan / referensi</th>
                    <th className="py-2 pr-3">Waktu</th>
                  </tr>
                </thead>
                <tbody>
                  {trail.map((line, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-[color:var(--kkp-border)]"
                    >
                      <td className="py-2 pr-3 font-semibold">{line.action}</td>
                      <td className="py-2 pr-3">
                        {line.fromStage} → {line.toStage}
                      </td>
                      <td className="py-2 pr-3">{line.actor ?? "—"}</td>
                      <td className="py-2 pr-3">
                        {line.reason ?? "—"}
                        {line.evidenceRef ? ` (${line.evidenceRef})` : ""}
                        {line.authorityRef
                          ? ` · order ${line.authorityRef}`
                          : ""}
                      </td>
                      <td className="py-2 pr-3 text-[color:var(--kkp-text-muted)]">
                        {formatDateTime(line.decidedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle as="h3">Keputusan</CardTitle>
        </CardHeader>
        <CardBody>
          {actions.length === 0 ? (
            <div className="text-sm text-[color:var(--kkp-text-muted)]">
              Kasus ini sudah ditutup — tidak ada tindakan lanjutan.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {actions.map((action) =>
                action === "acknowledge" ? (
                  <Button
                    key={action}
                    variant={ACTION_VARIANT[action]}
                    disabled={!fdsEnabled || acknowledgeMutation.isPending}
                    onClick={() => acknowledgeMutation.mutate()}
                  >
                    {ACTION_LABELS[action]}
                  </Button>
                ) : (
                  <Button
                    key={action}
                    variant={ACTION_VARIANT[action]}
                    disabled={!fdsEnabled}
                    onClick={() => setPendingAction(action)}
                  >
                    {ACTION_LABELS[action]}
                  </Button>
                ),
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Dialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <DialogContent>
          {pendingAction ? (
            <DecisionForm
              caseId={caseId}
              action={pendingAction}
              fdsEnabled={fdsEnabled}
              onClose={() => setPendingAction(null)}
              onDecided={(result) => {
                setPendingAction(null);
                refresh();
                toast.success(
                  result.paidRecipients !== null
                    ? `Keputusan disimpan. ${result.paidRecipients} penerima dibayarkan.`
                    : "Keputusan disimpan.",
                  { title: "Tersimpan" },
                );
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputClassName =
  "w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:rgba(255,255,255,0.06)] px-3 py-2.5 text-[color:rgba(255,255,255,0.92)] placeholder:text-[color:rgba(255,255,255,0.35)] outline-none transition focus:border-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)] focus:shadow-[0_0_0_3px_rgba(203,72,226,0.18)]";

function DecisionForm({
  caseId,
  action,
  fdsEnabled,
  onClose,
  onDecided,
}: {
  caseId: string;
  action: FdsAction;
  fdsEnabled: boolean;
  onClose: () => void;
  onDecided: (result: FdsDecisionResult) => void;
}) {
  const [reason, setReason] = React.useState("");
  const [evidenceRef, setEvidenceRef] = React.useState("");
  const [authorityRef, setAuthorityRef] = React.useState("");
  const requiresAuthority = needsAuthorityRef(action);

  const mutation = useMutation({
    mutationFn: () =>
      decideFdsCase(caseId, action, {
        reason: reason.trim(),
        evidenceRef: evidenceRef.trim(),
        authorityRef: authorityRef.trim(),
      }),
    onSuccess: onDecided,
    onError: (err: unknown) => {
      toast.danger(
        err instanceof ApiError ? err.message : "Gagal menyimpan keputusan.",
        { title: "Error" },
      );
    },
  });

  const isValid =
    reason.trim() !== "" &&
    evidenceRef.trim() !== "" &&
    (!requiresAuthority || authorityRef.trim() !== "");

  return (
    <>
      <DialogHeader>
        <DialogTitle>{ACTION_LABELS[action]}</DialogTitle>
        <DialogDescription>
          Alasan dan referensi back-office wajib diisi dan dicatat pada jejak
          kasus.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-3">
        <FormField label="Alasan" required>
          <textarea
            rows={3}
            className={inputClassName}
            placeholder="Wajib diisi — alasan keputusan ini"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <FormField label="Referensi back-office" required hint="mis. BO-015">
          <input
            className={inputClassName}
            value={evidenceRef}
            onChange={(e) => setEvidenceRef(e.target.value)}
          />
        </FormField>
        {requiresAuthority ? (
          <FormField label="Referensi kewenangan (surat resmi)" required>
            <input
              className={inputClassName}
              value={authorityRef}
              onChange={(e) => setAuthorityRef(e.target.value)}
            />
          </FormField>
        ) : null}
      </div>

      <DialogFooter>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Batal
        </Button>
        <Button
          variant={ACTION_VARIANT[action]}
          type="button"
          disabled={!fdsEnabled || !isValid || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Menyimpan…" : "Simpan keputusan"}
        </Button>
      </DialogFooter>
    </>
  );
}

export default function FdsCaseDetailPage() {
  const params = useParams();
  const caseId = params.caseId ?? "";
  const { data: capabilities } = useCapabilities();
  return (
    <PageContainer
      title="Peninjauan kasus FDS"
      description={caseId ? `ID: ${caseId}` : "No case ID provided."}
      width="wide"
      padding="lg"
    >
      <FdsInactiveBanner fds={capabilities?.fds} />
      <FdsCaseDetail caseId={caseId} />
    </PageContainer>
  );
}
